import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import svg from "../../utils/svg"
import styles from "./FlipsidePaywall.module.scss"

/**
 * FlipsidePaywall (`/flipside`) — an original, self-contained FEATURE-SPECIFIC
 * paywall for Private Photo Vault Premium. Paywall type rolled at random for this
 * run: FEATURE-SPECIFIC.
 *
 * ── Style directive (read live from #paywalls each run) ─────────────────────
 * The newest @Paywalls-Bot mention this run was `focus on security and privacy`
 * (NOT the reset keyword `default`), so it is an ACTIVE STANDING DIRECTIVE at
 * super-high priority. The whole concept is built to it — privacy told through
 * the single most ordinary security moment there is: the instant you set your
 * phone down.
 *
 * ── Concept ────────────────────────────────────────────────────────────────
 * "The flip side is yours." One real Premium safeguard, headlined: FACEDOWN
 * LOCK. Someone asks to see a photo. You hand the phone over — or just lay it on
 * the table. The moment it goes face-down, the vault seals itself: the screen
 * frosts to private, the memories vanish from view, and the only-you lock is
 * already armed before anyone can swipe. Turn it back over and it's yours again.
 * No PIN scramble, no panic tap — the gesture you already make a hundred times a
 * day IS the lock.
 *
 * ── Interaction (the hero mechanic) ─────────────────────────────────────────
 * A single phone rests on a tabletop, face-up, showing your vault. GRAB IT AND
 * TIP IT FACE-DOWN — a real 3-D turn-over: the device rotates on its bottom
 * edge, the contact shadow sharpens as it meets the table, a privacy frost
 * sweeps across the screen, the grid dims to nothing, and a lock-charm clicks as
 * FACEDOWN LOCK arms. Lift it back up to reveal the memories again. True two-way
 * physics: release past the tipping point and it snaps flat & sealed; release
 * near upright and it springs back face-up.
 *
 * ── Divergence (vs. the 95+ prior concepts, incl. the security cluster) ─────
 * Layout      : a lone device on a TABLETOP with a live contact shadow — not a
 *               folio (Seam), a pattern grid (Keyline), rings (Sanctum), a
 *               cipher wall (Static), a darkroom (Safelight) or a crest (Ward).
 * Interaction : TURN IT OVER — a 3-D rotateX tip onto its face. New to the whole
 *               set: not a zip pull (Seam), pattern trace (Keyline), morph slider
 *               (Bystander), dial (Tumbler), cord-pull (Safelight) or tap-pulse
 *               (Vigil).
 * Metaphor    : the flip side — what faces the table is private.
 * Value frame : the HANDOFF moment + risk reversal — hand it over, nothing shows.
 * Register    : everyday relief / trust — the calm of setting it down.
 * CTA         : "Turn on Facedown Lock" — yearly featured.
 *
 * ── Honest & App Store compliant ───────────────────────────────────────────
 * Facedown Lock is a REAL Private Photo Vault feature (the app icon set ships a
 * facedown-lock glyph). It is the headline; unlocking Premium turns it on along
 * with the other real safeguards named (only-you lock · encrypted Cloud Vault ·
 * break-in snapshots · app disguise), each shown honestly. No countdown, no fake
 * scarcity, no data-loss threat, no invented feature or guarantee. "Save 79%" is
 * real arithmetic from live product data. Yearly is the featured, default, most
 * prominent price; monthly is small and secondary. The 7-day free trial is the
 * real trial. Renders on built-in debug product data with bundled photos — no
 * native injection, no external egress.
 */

// The vault, shown on the phone's face — bundled, self-contained memory photos
// reused from the repo. A 3×3 so it reads as "a whole album" at a glance.
const VAULT = [
  "/keyline/k1.jpg",
  "/keyline/k2.jpg",
  "/keyline/k3.jpg",
  "/keyline/k4.jpg",
  "/keyline/k5.jpg",
  "/keyline/k6.jpg",
  "/keyline/k7.jpg",
  "/keyline/k8.jpg",
  "/keyline/k9.jpg",
]

// The other real safeguards that come switched on with Premium. Facedown Lock is
// the hero above; these are the supporting cast, kept deliberately secondary.
const ALSO = [
  {
    key: "lock",
    Icon: svg.featureIcons.PatternLock,
    name: "Only-you lock",
    line: "Pattern, Face ID or a code only you know",
  },
  {
    key: "cloud",
    Icon: svg.featureIcons.CloudVault,
    name: "Encrypted Cloud Vault",
    line: "A private, encrypted backup of every memory",
  },
  {
    key: "breakin",
    Icon: svg.featureIcons.Breakin,
    name: "Break-in snapshots",
    line: "A silent photo of anyone who tries a wrong code",
  },
  {
    key: "disguise",
    Icon: svg.featureIcons.Disguise,
    name: "App disguise",
    line: "Hide the whole vault behind an ordinary app",
  },
]

const clamp01 = (v: number) => Math.max(0, Math.min(1, v))

// How far (in px) a full turn-over travels under the finger.
const DRAG_RANGE = 240

const FlipsidePaywall = () => {
  const { state, actions } = useAppState()
  const reduceMotion = useReducedMotion()

  // Blend the safe-area insets into the room so the scene runs edge to edge.
  useEffect(() => {
    actions.setHeaderInsetColor("#0b0e16")
    actions.setFooterInsetColor("#080a11")
  }, [actions])

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$3.00"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"
  const trialDays = yearlyProduct?.introPricing?.period ?? 7

  const savingsPct = useMemo(() => {
    if (!yearlyProduct || !monthlyProduct) return 79
    const p = Math.round(
      (1 - yearlyProduct.price / (monthlyProduct.price * 12)) * 100,
    )
    return p > 0 ? p : 0
  }, [yearlyProduct, monthlyProduct])

  const [plan, setPlan] = useState<"yearly" | "monthly">("yearly")

  // ── turn-over state ───────────────────────────────────────────────────────
  // t: 0 = upright / face-up (memories showing), 1 = laid flat / face-down
  // (sealed). Locked once past the tipping point.
  const [t, setT] = useState(0)
  const tRef = useRef(0)
  const dragging = useRef(false)
  const startY = useRef(0)
  const startT = useRef(0)
  const userActed = useRef(false)
  const raf = useRef<number | null>(null)

  const locked = t >= 0.9

  const setTVal = useCallback((v: number) => {
    const c = clamp01(v)
    tRef.current = c
    setT(c)
  }, [])

  const stopRaf = useCallback(() => {
    if (raf.current != null) cancelAnimationFrame(raf.current)
    raf.current = null
  }, [])

  // Ease t toward a target (auto-demo + snap-on-release). Cancels the moment the
  // user grabs the device mid-flight.
  const animateTo = useCallback(
    (target: number, ms: number, onDone?: () => void) => {
      stopRaf()
      if (reduceMotion) {
        setTVal(target)
        onDone?.()
        return
      }
      const from = tRef.current
      const start = performance.now()
      const ease = (x: number) => 1 - Math.pow(1 - x, 3)
      const step = (now: number) => {
        if (dragging.current) return
        const k = Math.min(1, (now - start) / ms)
        setTVal(from + (target - from) * ease(k))
        if (k < 1) raf.current = requestAnimationFrame(step)
        else {
          raf.current = null
          onDone?.()
        }
      }
      raf.current = requestAnimationFrame(step)
    },
    [reduceMotion, setTVal, stopRaf],
  )

  // Auto-demo: turn it face-down once so the payoff reads instantly, hold a beat
  // sealed, then lift it back up and hand it to the user.
  useEffect(() => {
    if (window.location.search.includes("nodemo")) return
    const timers: number[] = []
    timers.push(
      window.setTimeout(() => {
        if (!userActed.current) animateTo(1, 1250)
      }, 900),
    )
    timers.push(
      window.setTimeout(() => {
        if (!userActed.current) animateTo(0.18, 900)
      }, 2900),
    )
    return () => {
      timers.forEach((x) => window.clearTimeout(x))
      stopRaf()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduceMotion])

  // ── manual turn-over ──────────────────────────────────────────────────────
  const begin = useCallback(
    (e: React.PointerEvent) => {
      userActed.current = true
      stopRaf()
      dragging.current = true
      startY.current = e.clientY
      startT.current = tRef.current
      ;(e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId)
    },
    [stopRaf],
  )

  const move = useCallback(
    (e: React.PointerEvent) => {
      if (!dragging.current) return
      const delta = (e.clientY - startY.current) / DRAG_RANGE
      setTVal(startT.current + delta)
    },
    [setTVal],
  )

  const end = useCallback(() => {
    if (!dragging.current) return
    dragging.current = false
    const v = tRef.current
    // Tipping-point physics: past the balance point it falls flat & seals;
    // short of it, it rights itself face-up.
    if (v > 0.55) animateTo(1, 300)
    else animateTo(0, 300)
  }, [animateTo])

  const toggle = useCallback(() => {
    userActed.current = true
    animateTo(tRef.current >= 0.5 ? 0 : 1, 460)
  }, [animateTo])

  const handleSubscribe = useCallback(() => {
    const product = plan === "yearly" ? yearlyProduct : monthlyProduct
    if (product) native.subscribe(product)
    else native.postNativeEvent("subscribe")
  }, [plan, yearlyProduct, monthlyProduct])

  return (
    <div
      className={`${styles.root} ${locked ? styles.isLocked : ""}`}
      style={{ ["--t" as any]: t }}
    >
      <div className={styles.scroll}>
        {/* ── header ─────────────────────────────────────────────────────── */}
        <header className={styles.top}>
          <div className={styles.brand}>
            <span className={styles.brandMark} aria-hidden="true">
              <svg viewBox="0 0 24 24" width="15" height="15">
                <path
                  d="M12 2.6l6.8 2.6v5.3c0 4.4-2.9 8-6.8 9.4-3.9-1.4-6.8-5-6.8-9.4V5.2L12 2.6z"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinejoin="round"
                />
                <path
                  d="M9 12.2l2.2 2.2L15 10.4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            <span className={styles.brandName}>
              Private Photo Vault
              <span className={styles.brandTag}>Premium</span>
            </span>
          </div>
          <button
            className={styles.close}
            onClick={() => native.postNativeEvent("close")}
            aria-label="Close"
          >
            <svg viewBox="0 0 24 24" width="15" height="15">
              <path
                d="M6 6l12 12M18 6L6 18"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </header>

        {/* ── intro ──────────────────────────────────────────────────────── */}
        <section className={styles.intro}>
          <span className={styles.kicker}>
            <span className={styles.kickerDot} aria-hidden="true" />
            Premium safeguard · Facedown Lock
          </span>
          <h1 className={styles.title}>
            {locked ? (
              <>
                Face-down.
                <br />
                <span className={styles.accent}>Already private.</span>
              </>
            ) : (
              <>
                Hand it over.
                <br />
                <span className={styles.accent}>The flip side is yours.</span>
              </>
            )}
          </h1>
          <p className={styles.sub}>
            The instant your phone goes face-down, the vault seals itself — no
            tap, no scramble. The gesture you already make is the lock.
          </p>
        </section>

        {/* ── the tabletop scene (hero) ──────────────────────────────────── */}
        <section className={styles.stage}>
          <div
            className={styles.room}
            onPointerDown={begin}
            onPointerMove={move}
            onPointerUp={end}
            onPointerCancel={end}
            role="button"
            tabIndex={0}
            aria-label="Turn the phone face-down to seal the vault"
          >
            {/* contact shadow that sharpens as the phone meets the table */}
            <span className={styles.shadow} aria-hidden="true" />

            {/* the device — a real 3-D turn-over */}
            <div className={styles.deviceWrap}>
              <div className={styles.device}>
                {/* FRONT: the screen / vault */}
                <div className={styles.faceFront}>
                  <span className={styles.notch} aria-hidden="true" />
                  <div className={styles.statusRow} aria-hidden="true">
                    <span>Vault</span>
                    <span className={styles.statusDot} />
                  </div>
                  <div className={styles.grid}>
                    {VAULT.map((src, i) => (
                      <span
                        key={i}
                        className={styles.tile}
                        style={{
                          backgroundImage: `url(${src})`,
                          ["--i" as any]: i,
                        }}
                      />
                    ))}
                  </div>

                  {/* privacy frost that sweeps in as it tips face-down */}
                  <span className={styles.frost} aria-hidden="true">
                    <span className={styles.frostLock}>
                      <svg viewBox="0 0 24 24" width="26" height="26">
                        <rect
                          x="5"
                          y="10.5"
                          width="14"
                          height="10"
                          rx="2.4"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.8"
                        />
                        <path
                          d="M8 10.5V8a4 4 0 0 1 8 0v2.5"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                        />
                      </svg>
                      <span>Private</span>
                    </span>
                  </span>
                </div>

                {/* BACK: what the room sees once it's face-down */}
                <div className={styles.faceBack} aria-hidden="true">
                  <span className={styles.lens} />
                  <span className={styles.lensSmall} />
                  <span className={styles.backGlyph}>
                    <svg viewBox="0 0 25 25" width="30" height="30">
                      <rect
                        x="4"
                        y="3"
                        width="10"
                        height="19"
                        rx="2.2"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                      />
                      <rect
                        x="12"
                        y="9.5"
                        width="9"
                        height="7.5"
                        rx="1.6"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                      />
                      <path
                        d="M14 9.5V8.4a2.6 2.6 0 0 1 5.2 0V9.5"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                    </svg>
                  </span>
                  <span className={styles.backLabel}>Facedown Lock</span>
                </div>
              </div>
            </div>

            {/* the seal-charm that clicks home when it lands flat */}
            <span
              className={`${styles.charm} ${locked ? styles.charmOn : ""}`}
              aria-hidden="true"
            >
              <svg viewBox="0 0 24 24" width="14" height="14">
                <path
                  d="M5 12.5l4.2 4.2L19 7"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </div>

          {/* status plate */}
          <div
            className={`${styles.plate} ${locked ? styles.plateOn : styles.plateOff}`}
          >
            <span className={styles.plateDot} />
            {locked ? "SEALED · FACE-DOWN" : "FACE-UP · TIP TO SEAL"}
          </div>

          <button
            type="button"
            className={styles.hint}
            onClick={toggle}
            aria-label={locked ? "Turn face-up" : "Turn face-down"}
          >
            {locked ? "Turn it back over" : "Turn it face-down"}
          </button>
        </section>

        {/* ── the everyday proof (honest framing of the hero feature) ─────── */}
        <p className={styles.proof}>
          <span className={styles.proofMark} aria-hidden="true">
            <svg viewBox="0 0 24 24" width="13" height="13">
              <path
                d="M12 2.6l6.6 2.5v5.2c0 4.3-2.8 7.7-6.6 9.1-3.8-1.4-6.6-4.8-6.6-9.1V5.1L12 2.6z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          Set it on the table, pass it across, drop it in a bag — the moment
          it's face-down your memories are already gone from the screen.
        </p>

        {/* ── comes switched on with Premium (supporting, secondary) ─────── */}
        <ul className={styles.also}>
          <li className={styles.alsoHead}>
            <span>Switched on with Premium</span>
            <span className={styles.alsoCount}>
              {ALSO.length + 1} safeguards
            </span>
          </li>
          <li className={`${styles.alsoRow} ${styles.alsoHero}`}>
            <span className={styles.alsoIcon} aria-hidden="true">
              <svg viewBox="0 0 25 25" width="17" height="17">
                <rect
                  x="4"
                  y="3"
                  width="9"
                  height="18"
                  rx="2"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                />
                <rect
                  x="12"
                  y="9"
                  width="9"
                  height="7.5"
                  rx="1.6"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                />
                <path
                  d="M14 9V7.9a2.6 2.6 0 0 1 5.2 0V9"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                />
              </svg>
            </span>
            <span className={styles.alsoText}>
              <span className={styles.alsoName}>Facedown Lock</span>
              <span className={styles.alsoLine}>
                Seals the vault the instant the phone turns over
              </span>
            </span>
            <span className={styles.alsoBadge}>This screen</span>
          </li>
          {ALSO.map((g) => (
            <li key={g.key} className={styles.alsoRow}>
              <span className={styles.alsoIcon} aria-hidden="true">
                <g.Icon width={17} height={17} />
              </span>
              <span className={styles.alsoText}>
                <span className={styles.alsoName}>{g.name}</span>
                <span className={styles.alsoLine}>{g.line}</span>
              </span>
              <span className={styles.alsoTick} aria-hidden="true">
                <svg viewBox="0 0 24 24" width="13" height="13">
                  <path
                    d="M5 12.5l4.2 4.2L19 7"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </li>
          ))}
        </ul>

        {/* ── pricing: yearly featured, monthly small & secondary ────────── */}
        <div className={styles.plans}>
          <button
            className={`${styles.yearly} ${
              plan === "yearly" ? styles.selected : ""
            }`}
            onClick={() => setPlan("yearly")}
            aria-pressed={plan === "yearly"}
          >
            <span className={styles.yearlyGlow} aria-hidden="true" />
            <span className={styles.yearlyHead}>
              <span className={styles.yearlyName}>
                Premium · 1 year
                <span className={styles.trialPill}>
                  {trialDays}-day free trial
                </span>
              </span>
              {savingsPct > 0 && (
                <span className={styles.saveTag}>Save {savingsPct}%</span>
              )}
            </span>
            <span className={styles.yearlyPriceRow}>
              <span className={styles.yearlyPrice}>{yearlyPrice}</span>
              <span className={styles.yearlyUnit}>/ year</span>
              <span className={styles.yearlyPerMo}>≈ {yearlyPerMonth}/mo</span>
              <span className={styles.check} aria-hidden="true">
                <svg viewBox="0 0 24 24" width="13" height="13">
                  <path
                    d="M5 12.5l4.2 4.2L19 7"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </span>
            <span className={styles.yearlyFoot}>
              Facedown Lock and every safeguard, on for a full year
            </span>
          </button>

          <button
            className={`${styles.monthly} ${
              plan === "monthly" ? styles.monthlySel : ""
            }`}
            onClick={() => setPlan("monthly")}
            aria-pressed={plan === "monthly"}
          >
            <span>Or monthly</span>
            <span className={styles.monthlyPrice}>{monthlyPrice}/mo</span>
          </button>
        </div>

        <button className={styles.cta} onClick={handleSubscribe}>
          <span className={styles.ctaLabel}>
            {plan === "yearly"
              ? `Turn on Facedown Lock — ${trialDays}-day free trial`
              : "Turn on Facedown Lock"}
          </span>
          <span className={styles.ctaSub}>
            {plan === "yearly"
              ? `Then ${yearlyPrice}/year · cancel anytime`
              : `${monthlyPrice}/month · cancel anytime`}
          </span>
        </button>

        <div className={styles.footer}>
          <button onClick={() => native.postNativeEvent("already_purchased")}>
            Restore
          </button>
          <span className={styles.dot}>·</span>
          <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
          <span className={styles.dot}>·</span>
          <button onClick={() => native.postNativeEvent("privacy")}>
            Privacy
          </button>
        </div>
      </div>
    </div>
  )
}

export default FlipsidePaywall
